import { brandStorage as localStorage } from '@/utils/brandSession';
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '../../utils/config';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { 
  Bell, 
  CalendarCheck, 
  Home, 
  PlayCircle, 
  LogIn, 
  LogOut,
  Loader2,
  ChevronRight,
  BookOpen,
  CheckCircle2,
  AlertCircle,
  FileDown
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { CalendarDays, TrendingUp, Euro } from 'lucide-react';
import { toast } from 'sonner';
import HomeSmartPropertyReportDialog from './HomeSmartPropertyReportDialog';
import { HOME_SMART_EMAIL_ONLY } from '../../utils/homeSmartRelease';
import { SampleDataCard } from '../../components/home-smart/SampleDataCard';

const isoDate = (date) => `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
const initialPeriod = () => {
  const now = new Date();
  return {
    month: `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`,
    from: isoDate(new Date(now.getFullYear(), now.getMonth(), 1)),
    to: isoDate(new Date(now.getFullYear(), now.getMonth() + 1, 0)),
    label: now.toLocaleDateString('it-IT', { month: '2-digit', year: 'numeric' }),
  };
};

const ResponsabileHome = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [userName, setUserName] = useState('');
  const [isExternal, setIsExternal] = useState(false);
  const [stats, setStats] = useState({
    arrivalsToday: 0,
    departuresToday: 0,
    totalProperties: 0,
    completedVideos: 0,
    totalVideos: 0,
    yearBookings: 0,
    yearRevenue: 0,
    occupancyPct: 0
  });
  const [notifications, setNotifications] = useState([]);
  const [recentBookings, setRecentBookings] = useState([]);
  const [dashboardProperties, setDashboardProperties] = useState([]);
  const [selectedPropertyId, setSelectedPropertyId] = useState('all');
  const [scopeLabel, setScopeLabel] = useState('Tutti gli immobili');
  const [scopeLoading, setScopeLoading] = useState(false);
  const [reportProperty, setReportProperty] = useState(null);
  const [periodType, setPeriodType] = useState('month');
  const [periodMonth, setPeriodMonth] = useState(initialPeriod().month);
  const [periodFrom, setPeriodFrom] = useState(initialPeriod().from);
  const [periodTo, setPeriodTo] = useState(initialPeriod().to);
  const [periodLabel, setPeriodLabel] = useState(initialPeriod().label);
  const [walletAlert, setWalletAlert] = useState(null);

  // Funzione per determinare il genere dal nome italiano
  const getGreeting = (fullName) => {
    if (!fullName) return 'Benvenuto';
    
    // Estrai il primo nome
    const firstName = fullName.split(' ')[0].toLowerCase().trim();
    
    // Lista di nomi femminili comuni che non finiscono in 'a'
    const femaleNames = ['carmen', 'irene', 'noemi', 'rachele', 'ruth', 'naomi', 'miriam', 'ester', 'agnes', 'beatrice', 'alice', 'elise', 'diane'];
    
    // Lista di nomi maschili che finiscono in 'a'
    const maleNamesEndingA = ['luca', 'nicola', 'andrea', 'mattia', 'elia', 'battista', 'enea', 'cosma', 'evangelista', 'tobia', 'isaia', 'osea', 'giona', 'geremia', 'ismaela'];
    
    // Se il nome è nella lista di eccezioni maschili
    if (maleNamesEndingA.includes(firstName)) {
      return 'Benvenuto';
    }
    
    // Se il nome è nella lista di eccezioni femminili
    if (femaleNames.includes(firstName)) {
      return 'Benvenuta';
    }
    
    // Regola generale: nomi che finiscono in 'a' sono femminili
    if (firstName.endsWith('a')) {
      return 'Benvenuta';
    }
    
    return 'Benvenuto';
  };

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const applyHomeSmartDashboard = (data) => {
    const nextStats = data?.stats || {};
    setDashboardProperties(data?.properties || []);
    setSelectedPropertyId(data?.selected_property_id || 'all');
    setScopeLabel(data?.scope_label || 'Tutti gli immobili');
    setPeriodLabel(data?.period_label || initialPeriod().label);
    setRecentBookings(data?.recent_bookings || []);
    setStats({
      arrivalsToday: nextStats.arrivals || nextStats.arrivals_today || 0,
      departuresToday: nextStats.departures || nextStats.departures_today || 0,
      totalProperties: nextStats.total_properties || 0,
      portfolioProperties: nextStats.portfolio_properties || 0,
      completedVideos: 0,
      totalVideos: 0,
      periodBookings: nextStats.period_bookings || nextStats.year_bookings || 0,
      periodRevenue: nextStats.period_revenue || nextStats.year_revenue || 0,
      occupancyPct: nextStats.occupancy_pct || 0,
    });
    const notifs = [];
    if (nextStats.arrivals > 0) {
      notifs.push({ id: 'arrival', icon: LogIn, color: 'bg-green-100 text-green-700', message: `${nextStats.arrivals} arrivi nel periodo`, action: () => navigate('/responsabile/arrivi-partenze') });
    }
    if (nextStats.departures > 0) {
      notifs.push({ id: 'departure', icon: LogOut, color: 'bg-orange-100 text-orange-700', message: `${nextStats.departures} partenze nel periodo`, action: () => navigate('/responsabile/arrivi-partenze') });
    }
    if ((data?.recent_bookings || []).length > 0) {
      notifs.push({ id: 'booking', icon: CalendarCheck, color: 'bg-blue-100 text-blue-700', message: `${data.recent_bookings.length} prenotazioni recenti nel riepilogo`, action: () => navigate('/responsabile/arrivi-partenze') });
    }
    setNotifications(notifs);
  };

  const loadHomeSmartDashboard = async (propertyId = 'all', initial = false) => {
    if (!initial) setScopeLoading(true);
    try {
      const token = localStorage.getItem('hw_token');
      const params = new URLSearchParams({ period_type: periodType });
      if (propertyId && propertyId !== 'all') params.set('property_id', propertyId);
      if (periodType === 'month') params.set('month', periodMonth);
      if (periodType === 'range') {
        params.set('date_from', periodFrom);
        params.set('date_to', periodTo);
      }
      const response = await axios.get(`${API}/api/home-smart/dashboard?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      applyHomeSmartDashboard(response.data);
    } catch (error) {
      console.error('Error loading Home Smart dashboard:', error);
      if (!initial) toast.error(error.response?.data?.detail || 'Impossibile aggiornare le statistiche');
    } finally {
      if (!initial) setScopeLoading(false);
    }
  };

  const applyDashboardPeriod = () => {
    if (periodType === 'month' && !periodMonth) {
      toast.error('Seleziona il mese da visualizzare');
      return;
    }
    if (periodType === 'range' && (!periodFrom || !periodTo || periodTo < periodFrom)) {
      toast.error('Controlla le date dell’intervallo');
      return;
    }
    loadHomeSmartDashboard(selectedPropertyId);
  };

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('hw_token');
      const headers = { Authorization: `Bearer ${token}` };

      // Get user info from localStorage for personalized greeting
      try {
        const userStr = localStorage.getItem('hw_user');
        if (userStr) {
          const user = JSON.parse(userStr);
          setUserName(user.full_name || user.first_name || '');
        }
      } catch (e) {
        console.log('Could not parse user info');
      }

      // Rileva tenant esterno Home Smart
      let isExt = false;
      try {
        const vres = await axios.get(`${API}/api/auth/verify`, { headers });
        isExt = !!vres.data?.user?.is_external_tenant;
        setIsExternal(isExt);
        if (isExt && vres.data?.user?.full_name) setUserName(vres.data.user.full_name);
      } catch (e) { /* ignora */ }

      if (isExt) {
        await loadHomeSmartDashboard('all', true);
        if (HOME_SMART_EMAIL_ONLY) return;
        try {
          const walletResponse = await axios.get(`${API}/api/home-smart/wallet`, {
            headers: { Authorization: `Bearer ${token}` },
          });
          setWalletAlert(walletResponse.data.pricing_configured && walletResponse.data.low_balance ? walletResponse.data : null);
        } catch (walletError) {
          console.warn('Wallet status unavailable', walletError);
        }
        return;
      }

      // Fetch MY properties (solo quelli assegnati al responsabile)
      const propsRes = await axios.get(`${API}/api/responsible/my-properties`, { headers });
      const myProperties = propsRes.data || [];
      const totalProperties = myProperties.length;

      // Fetch MY bookings (solo quelli dei miei immobili - filtrato server-side)
      const bookingsRes = await axios.get(`${API}/api/responsible/my-bookings`, { headers });
      const myBookings = bookingsRes.data || [];

      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const endOfToday = new Date(today);
      endOfToday.setHours(23, 59, 59, 999);

      const arrivalsToday = myBookings.filter(b => {
        if (b.cancelled) return false;
        const checkIn = new Date(b.check_in);
        return checkIn >= today && checkIn <= endOfToday;
      }).length;

      const departuresToday = myBookings.filter(b => {
        if (b.cancelled) return false;
        const checkOut = new Date(b.check_out);
        return checkOut >= today && checkOut <= endOfToday;
      }).length;

      // Get recent bookings (last 5 created in the last 7 days) - solo dei miei immobili
      const oneWeekAgo = new Date();
      oneWeekAgo.setDate(oneWeekAgo.getDate() - 7);
      const recent = myBookings
        .filter(b => new Date(b.created_at || b.check_in) >= oneWeekAgo)
        .sort((a, b) => {
          if (a.cancelled && !b.cancelled) return 1;
          if (!a.cancelled && b.cancelled) return -1;
          return new Date(b.created_at || b.check_in) - new Date(a.created_at || a.check_in);
        })
        .slice(0, 5);
      setRecentBookings(recent);

      // Fetch training progress
      let completedVideos = 0;
      let totalVideos = 0;
      if (!isExt) {
        try {
          const trainingRes = await axios.get(`${API}/api/training/progress`, { headers });
          completedVideos = trainingRes.data?.completed_videos || 0;
          totalVideos = trainingRes.data?.total_videos || 0;
        } catch (e) {
          // Training endpoint may not exist yet
        }
      }

      // KPI anno corrente (tenant esterni)
      const yearStart = new Date(today.getFullYear(), 0, 1);
      const activeYearBookings = myBookings.filter(b => !b.cancelled && new Date(b.check_in).getFullYear() === today.getFullYear());
      const yearBookings = activeYearBookings.length;
      const yearRevenue = activeYearBookings.reduce((s, b) => s + (parseFloat(b.guest_total) || 0), 0);
      let bookedNightsYTD = 0;
      myBookings.filter(b => !b.cancelled).forEach(b => {
        const ci = new Date(b.check_in); const co = new Date(b.check_out);
        const from = ci > yearStart ? ci : yearStart;
        const to = co < today ? co : today;
        if (to > from) bookedNightsYTD += Math.round((to - from) / 86400000);
      });
      const daysElapsed = Math.max(1, Math.round((today - yearStart) / 86400000));
      const occupancyPct = totalProperties > 0 ? Math.min(100, Math.round((bookedNightsYTD / (totalProperties * daysElapsed)) * 100)) : 0;

      // Build notifications
      const notifs = [];
      if (arrivalsToday > 0) {
        notifs.push({
          id: 1,
          type: 'arrival',
          icon: LogIn,
          color: 'bg-green-100 text-green-700',
          message: `${arrivalsToday} arriv${arrivalsToday === 1 ? 'o' : 'i'} oggi`,
          action: () => navigate('/responsabile/arrivi-partenze')
        });
      }
      if (departuresToday > 0) {
        notifs.push({
          id: 2,
          type: 'departure',
          icon: LogOut,
          color: 'bg-orange-100 text-orange-700',
          message: `${departuresToday} partenz${departuresToday === 1 ? 'a' : 'e'} oggi`,
          action: () => navigate('/responsabile/arrivi-partenze')
        });
      }
      if (totalVideos > 0 && completedVideos < totalVideos) {
        notifs.push({
          id: 3,
          type: 'training',
          icon: PlayCircle,
          color: 'bg-purple-100 text-purple-700',
          message: `${totalVideos - completedVideos} video da completare`,
          action: () => navigate('/responsabile/formazione')
        });
      }
      if (recent.length > 0) {
        notifs.push({
          id: 4,
          type: 'booking',
          icon: CalendarCheck,
          color: 'bg-blue-100 text-blue-700',
          message: `${recent.length} nuov${recent.length === 1 ? 'a prenotazione' : 'e prenotazioni'} questa settimana`,
          action: () => navigate('/responsabile/arrivi-partenze')
        });
      }
      setNotifications(notifs);

      setStats({
        arrivalsToday,
        departuresToday,
        totalProperties,
        completedVideos,
        totalVideos,
        yearBookings,
        yearRevenue,
        occupancyPct
      });
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    return date.toLocaleDateString('it-IT', { 
      day: '2-digit', 
      month: 'short'
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader2 className="h-8 w-8 animate-spin text-blue-500" />
        <span className="ml-3 text-slate-600">Caricamento dashboard...</span>
      </div>
    );
  }

  return (
    <div className="p-4 pt-14 md:pt-6 md:p-6 space-y-6" data-testid="responsabile-home">
      {/* Header */}
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-slate-800" data-testid="responsabile-home-title">
            {getGreeting(userName)}{userName ? `, ${userName.split(' ')[0]}` : ''}!
          </h1>
          <p className="text-slate-500 mt-1">{isExternal ? `Riepilogo del periodo ${periodLabel}` : 'Ecco il riepilogo di oggi'}</p>
        </div>
        <Button
          variant="outline"
          className={`bg-gradient-to-r from-purple-500 to-indigo-500 text-white border-0 hover:from-purple-600 hover:to-indigo-600 ${isExternal ? 'hidden' : ''}`}
          onClick={() => navigate('/responsabile/video-holly')}
          data-testid="holly-video-tutorial-button"
        >
          <PlayCircle className="h-4 w-4 mr-2" />
          Video Tutorial Holly
        </Button>
      </div>

      {isExternal && (
        <div className="grid gap-5 border border-blue-100 bg-gradient-to-r from-blue-50 via-white to-emerald-50 p-4 lg:grid-cols-[minmax(0,1fr)_minmax(0,1.35fr)_auto] lg:items-end" data-testid="home-smart-property-scope-panel">
          <div className="min-w-0">
            <p className="text-xs font-bold uppercase text-blue-700" data-testid="home-smart-scope-eyebrow">Statistiche Home Smart</p>
            <label className="mt-2 block text-sm font-medium text-slate-700" htmlFor="home-smart-property-scope">Immobili nel riepilogo</label>
            <Select value={selectedPropertyId} onValueChange={(value) => loadHomeSmartDashboard(value)} disabled={scopeLoading}>
              <SelectTrigger id="home-smart-property-scope" className="mt-1 w-full sm:max-w-md" data-testid="home-smart-property-scope-select">
                <SelectValue placeholder="Scegli un immobile" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all" data-testid="home-smart-property-scope-all-option">Tutti i miei immobili</SelectItem>
                {dashboardProperties.map((property) => (
                  <SelectItem key={property.id} value={property.id} data-testid={`home-smart-property-scope-option-${property.id}`}>
                    {property.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="mt-2 text-sm text-slate-500" data-testid="home-smart-scope-label">Vista: {scopeLabel}</p>
          </div>
          <div className="min-w-0 space-y-2" data-testid="home-smart-dashboard-period-panel">
            <Label>Periodo delle statistiche</Label>
            <div className="grid gap-2 sm:grid-cols-[11rem_1fr]">
              <Select value={periodType} onValueChange={setPeriodType} disabled={scopeLoading}>
                <SelectTrigger data-testid="home-smart-dashboard-period-type"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="month" data-testid="home-smart-dashboard-period-month-option">Mese</SelectItem>
                  <SelectItem value="range" data-testid="home-smart-dashboard-period-range-option">Intervallo</SelectItem>
                </SelectContent>
              </Select>
              {periodType === 'month' ? (
                <Input type="month" value={periodMonth} onChange={(event) => setPeriodMonth(event.target.value)} data-testid="home-smart-dashboard-month-input" />
              ) : (
                <div className="grid grid-cols-2 gap-2">
                  <Input type="date" aria-label="Data iniziale statistiche" value={periodFrom} onChange={(event) => setPeriodFrom(event.target.value)} data-testid="home-smart-dashboard-date-from-input" />
                  <Input type="date" aria-label="Data finale statistiche" value={periodTo} onChange={(event) => setPeriodTo(event.target.value)} data-testid="home-smart-dashboard-date-to-input" />
                </div>
              )}
            </div>
            <Button variant="outline" onClick={applyDashboardPeriod} disabled={scopeLoading} data-testid="home-smart-dashboard-apply-period-button">
              <CalendarDays className="mr-2 h-4 w-4" /> Aggiorna statistiche
            </Button>
          </div>
          <div className="flex min-h-10 items-center lg:justify-end">
            {scopeLoading ? (
              <div className="flex items-center gap-2 text-sm text-slate-500" data-testid="home-smart-scope-loading"><Loader2 className="h-4 w-4 animate-spin" /> Aggiornamento</div>
            ) : selectedPropertyId !== 'all' ? (
              <Button className="w-full bg-emerald-700 hover:bg-emerald-800 sm:w-auto" onClick={() => setReportProperty(dashboardProperties.find((property) => property.id === selectedPropertyId))} data-testid="home-smart-dashboard-report-button">
                <FileDown className="mr-2 h-4 w-4" /> Report PDF
              </Button>
            ) : null}
          </div>
        </div>
      )}
      {isExternal && !HOME_SMART_EMAIL_ONLY && walletAlert && (
        <button onClick={() => navigate('/responsabile/comunicazioni')} className="mt-4 flex w-full items-start gap-3 border border-amber-200 bg-amber-50 p-4 text-left text-sm text-amber-900 transition-[border-color,background-color] hover:border-amber-400 hover:bg-amber-100" data-testid="dashboard-wallet-alert">
          <AlertCircle className="mt-0.5 h-5 w-5 shrink-0" />
          <span><b>{walletAlert.exhausted ? 'Credito invii automatici esaurito.' : 'Credito invii automatici sotto €5.'}</b> Saldo del tuo account: {Number(walletAlert.balance || 0).toLocaleString('it-IT', { style: 'currency', currency: 'EUR' })}. Apri Comunicazioni per ricaricare.</span>
        </button>
      )}
      {isExternal && <SampleDataCard onChanged={() => loadHomeSmartDashboard('all')} />}

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card 
          className="cursor-pointer hover:shadow-lg transition-shadow border-l-4 border-l-green-500"
          onClick={() => navigate('/responsabile/arrivi-partenze')}
          data-testid="card-arrivals-today"
        >
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">{isExternal ? 'Arrivi nel periodo' : 'Arrivi Oggi'}</p>
                <p className="text-3xl font-bold text-slate-800" data-testid="dashboard-arrivals-value">{stats.arrivalsToday}</p>
              </div>
              <div className="p-3 bg-green-100 rounded-full">
                <LogIn className="h-6 w-6 text-green-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card 
          className="cursor-pointer hover:shadow-lg transition-shadow border-l-4 border-l-orange-500"
          onClick={() => navigate('/responsabile/arrivi-partenze')}
          data-testid="card-departures-today"
        >
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">{isExternal ? 'Partenze nel periodo' : 'Partenze Oggi'}</p>
                <p className="text-3xl font-bold text-slate-800" data-testid="dashboard-departures-value">{stats.departuresToday}</p>
              </div>
              <div className="p-3 bg-orange-100 rounded-full">
                <LogOut className="h-6 w-6 text-orange-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card 
          className="cursor-pointer hover:shadow-lg transition-shadow border-l-4 border-l-blue-500"
          onClick={() => navigate('/responsabile/immobili')}
          data-testid="card-properties"
        >
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">{isExternal && selectedPropertyId !== 'all' ? 'Immobile selezionato' : 'Immobili Gestiti'}</p>
                <p className="text-3xl font-bold text-slate-800" data-testid="dashboard-properties-value">{stats.totalProperties}</p>
              </div>
              <div className="p-3 bg-blue-100 rounded-full">
                <Home className="h-6 w-6 text-blue-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        {isExternal ? (
        <Card 
          className="cursor-pointer hover:shadow-lg transition-shadow border-l-4 border-l-indigo-500"
          onClick={() => navigate('/responsabile/storico')}
          data-testid="card-year-bookings"
        >
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Prenotazioni nel periodo</p>
                <p className="text-3xl font-bold text-slate-800" data-testid="dashboard-year-bookings-value">{stats.periodBookings ?? stats.yearBookings}</p>
              </div>
              <div className="p-3 bg-indigo-100 rounded-full">
                <CalendarDays className="h-6 w-6 text-indigo-600" />
              </div>
            </div>
          </CardContent>
        </Card>
        ) : (
        <Card 
          className="cursor-pointer hover:shadow-lg transition-shadow border-l-4 border-l-purple-500"
          onClick={() => navigate('/responsabile/formazione')}
          data-testid="card-training"
        >
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Formazione</p>
                <p className="text-3xl font-bold text-slate-800">
                  {stats.completedVideos}/{stats.totalVideos || '0'}
                </p>
              </div>
              <div className="p-3 bg-purple-100 rounded-full">
                <BookOpen className="h-6 w-6 text-purple-600" />
              </div>
            </div>
          </CardContent>
        </Card>
        )}
      </div>

      {/* KPI Tenant (Home Smart) */}
      {isExternal && (
        <div className="grid grid-cols-2 gap-4" data-testid="tenant-kpi-row">
          <Card className="border-l-4 border-l-emerald-500" data-testid="card-occupancy">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">Occupazione nel periodo</p>
                  <p className="text-3xl font-bold text-slate-800" data-testid="dashboard-occupancy-value">{stats.occupancyPct}%</p>
                  <p className="text-xs text-slate-400 mt-0.5">notti occupate su quelle disponibili</p>
                </div>
                <div className="p-3 bg-emerald-100 rounded-full">
                  <TrendingUp className="h-6 w-6 text-emerald-600" />
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="border-l-4 border-l-amber-500" data-testid="card-revenue">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">Incassi nel periodo</p>
                  <p className="text-3xl font-bold text-slate-800" data-testid="dashboard-revenue-value">
                    {(stats.periodRevenue ?? stats.yearRevenue ?? 0).toLocaleString('it-IT', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 })}
                  </p>
                  <p className="text-xs text-slate-400 mt-0.5">riproporzionati per le notti nel periodo</p>
                </div>
                <div className="p-3 bg-amber-100 rounded-full">
                  <Euro className="h-6 w-6 text-amber-600" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Notifications Section */}
      <Card data-testid="notifications-section">
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2">
            <Bell className="h-5 w-5 text-blue-600" />
            Notifiche
          </CardTitle>
        </CardHeader>
        <CardContent>
          {notifications.length === 0 ? (
            <div className="text-center py-8 text-slate-500">
              <CheckCircle2 className="h-12 w-12 mx-auto mb-3 text-green-500" />
              <p>Nessuna notifica - Tutto in ordine!</p>
            </div>
          ) : (
            <div className="space-y-3">
              {notifications.map(notif => {
                const Icon = notif.icon;
                return (
                  <div 
                    key={notif.id}
                    className={`flex items-center justify-between p-3 rounded-lg ${notif.color} cursor-pointer hover:opacity-80 transition-opacity`}
                    onClick={notif.action}
                    data-testid={`dashboard-notification-${notif.id}`}
                  >
                    <div className="flex items-center gap-3">
                      <Icon className="h-5 w-5" />
                      <span className="font-medium">{notif.message}</span>
                    </div>
                    <ChevronRight className="h-5 w-5" />
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Recent Bookings */}
      {recentBookings.length > 0 && (
        <Card data-testid="recent-bookings">
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2">
              <CalendarCheck className="h-5 w-5 text-blue-600" />
              {isExternal ? 'Prenotazioni nel periodo' : 'Prenotazioni Recenti'}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {recentBookings.map((booking, idx) => (
                <div 
                  key={booking.id || idx}
                  className={`flex items-center justify-between p-3 rounded-lg ${booking.cancelled ? 'bg-red-50 border border-red-200' : 'bg-slate-50'}`}
                  data-testid={`recent-booking-${booking.id || idx}`}
                >
                  <div>
                    <p className={`font-medium ${booking.cancelled ? 'text-red-600 line-through' : 'text-slate-800'}`}>{booking.guest_name || 'N/A'}</p>
                    <p className="text-sm text-slate-500">
                      {formatDate(booking.check_in)} - {formatDate(booking.check_out)}
                    </p>
                    {isExternal && booking.property_name && <p className="text-xs font-medium text-blue-700" data-testid={`recent-booking-property-${booking.id || idx}`}>{booking.property_name}</p>}
                  </div>
                  {booking.cancelled ? (
                    <Badge className="bg-red-600 text-white text-xs font-bold" data-testid={`badge-cancelled-recent-${booking.id || idx}`}>ANNULLATA</Badge>
                  ) : (
                    <Badge variant="outline" className="bg-white">
                      {booking.guests_count || 1} ospiti
                    </Badge>
                  )}
                </div>
              ))}
            </div>
            <Button 
              variant="outline" 
              className="w-full mt-4"
              onClick={() => navigate('/responsabile/arrivi-partenze')}
              data-testid="dashboard-all-bookings-button"
            >
              Vedi tutte le prenotazioni
              <ChevronRight className="h-4 w-4 ml-2" />
            </Button>
          </CardContent>
        </Card>
      )}
      <HomeSmartPropertyReportDialog open={!!reportProperty} onClose={() => setReportProperty(null)} property={reportProperty} />
    </div>
  );
};

export default ResponsabileHome;
